/**
 * Client-side: trigger propagation of profile (displayName, username, photoURL)
 * to all denormalized copies (feedback posts, comments, notifications).
 * Call after saving profile so name/avatar updates show everywhere.
 *
 * Optional: pass getToken to send Bearer auth (e.g. () => auth.currentUser?.getIdToken()).
 */
const PROPAGATE_API = '/api/profile/propagate';
const TIMEOUT_MS = 15_000;

export type TriggerProfilePropagationOptions = {
  uid: string;
  getToken?: () => Promise<string | null>;
};

export async function triggerProfilePropagation(
  uidOrOptions: string | TriggerProfilePropagationOptions
): Promise<{ ok: boolean; error?: string; result?: { postsUpdated: number; commentsUpdated: number; notificationsUpdated: number } }> {
  const uid = typeof uidOrOptions === 'string' ? uidOrOptions : uidOrOptions.uid;
  const getToken = typeof uidOrOptions === 'string' ? undefined : uidOrOptions.getToken;
  if (!uid?.trim()) return { ok: false, error: 'No uid' };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    let token: string | null = null;
    if (getToken) token = await getToken();
    const res = await fetch(PROPAGATE_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ userId: uid.trim() }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn('[profile-propagate] non-ok', res.status, data);
      return { ok: false, error: (data as { error?: string }).error || res.statusText };
    }
    return { ok: true, result: (data as { result?: typeof data }).result };
  } catch (err) {
    clearTimeout(timeoutId);
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('[profile-propagate] failed', msg);
    return { ok: false, error: msg };
  }
}
